import { useQuery } from '@tanstack/react-query';
import { Card } from '@zenid/web';
import { useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { css } from '../../../styled-system/css';
import { flex } from '../../../styled-system/patterns';
import { integrationQueries } from '../../api/queryKeys/integrationKeys';
import { Unauthorized } from '../../components/Error/Unauthorized';
import { ChangeForm } from '../../features/emailChange/components/ChangeForm';
import { isHighschoolEmail } from '../../features/highschool';
import { signInToIntegrationApp } from '../../features/integration/additional-sign-in';
import { IntegrationEmailSendComplete } from '../../features/integration/components/IntefrationEmailSendComplete';
import type { IntegrationSignInGuideOrigin } from '../../features/integration/components/IntegrationSignInGuide';
import { IntegrationSignInGuide } from '../../features/integration/components/IntegrationSignInGuide';
import { IntegrationSpecialAuditorNotice } from '../../features/integration/components/IntegrationSpecialAuditorNotice';
import { useIntegrationForm } from '../../features/integration/hooks/useIntegrationForm';
import { useLoginRequired } from '../../utils/auth/useLoginRequired';
import { useTitle } from '../../utils/useTitle';
import { useAuth } from '#/utils/auth/useAuth';

export function IntegrationFormPage() {
  useLoginRequired();
  useTitle('サブメールアドレス追加');
  const [isError, setIsError] = useState(false);
  // 統合ログインへ進む前に「統合される」旨を案内するクッション画面の表示状態。null 以外なら案内画面を表示する
  const [signInGuide, setSignInGuide] = useState<IntegrationSignInGuideOrigin | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { user } = useAuth();
  // useLoginRequired で user が存在する前提でチェックする
  if (user && !isHighschoolEmail(user.email)) {
    navigate('/unauthorized');
  }

  // 統合完了後にログイン元アプリへ戻すため、Actionが付与した client_id を callbackClientId として引き回す
  const callbackClientId = searchParams.get('client_id') ?? undefined;

  // 入力されたメールアドレスが既存アカウントだった場合の案内画面。ログイン先を絞り込むため入力メールを login_hint に引き回す
  const showRegisteredEmailGuide = useCallback((email: string) => {
    setSignInGuide({ type: 'registeredEmail', email });
  }, []);

  // 「個人のZEN IDを既にお持ちの方はこちら」からの案内画面。ログイン先のメールアドレスは不明なため login_hint は渡さない
  const showOwnedAccountGuide = useCallback(() => {
    setSignInGuide({ type: 'ownedAccount' });
  }, []);

  // 取得に失敗した場合はサブメールアドレス追加フォームをそのまま表示し、フォームを塞いだまま詰まないようにする
  const { data: specialAuditorAccount, isPending: isSpecialAuditorAccountPending } = useQuery({
    ...integrationQueries.specialAuditorAccount(),
    retry: false,
  });
  const hasSpecialAuditorAccount = specialAuditorAccount?.data?.exists === true;

  // 案内画面のボタン押下で、実際に統合ログインへ遷移する
  const proceedToIntegrationAppSignIn = useCallback(async () => {
    const screenHint = searchParams.get('screen_hint') ?? undefined;
    // 既存アカウント案内では入力メールを、特別聴講生アカウント検出時は検出アカウントのメールを login_hint に渡す
    const loginHint = signInGuide?.type === 'registeredEmail' ? signInGuide.email : specialAuditorAccount?.data?.email;
    await signInToIntegrationApp({ screenHint, loginHint }).catch(() => {
      setIsError(true);
    });
  }, [searchParams, signInGuide, specialAuditorAccount]);

  const { onSubmit, isSuccess, register, formState, email } = useIntegrationForm({
    onExistingAccount: showRegisteredEmailGuide,
    callbackClientId,
  });

  if (isError) {
    return <Unauthorized />;
  }
  if (signInGuide) {
    return <IntegrationSignInGuide origin={signInGuide} proceed={proceedToIntegrationAppSignIn} />;
  }
  if (isSuccess) {
    return <IntegrationEmailSendComplete email={email} />;
  }
  // 判定が確定する前にフォームを見せると、統合へ誘導すべきユーザーが入力を始めてしまうため待つ
  if (isSpecialAuditorAccountPending) {
    return null;
  }
  // 特別聴講生アカウントが存在する場合は新規アカウントを重複して作らせないためフォームを出さない
  if (hasSpecialAuditorAccount) {
    return <IntegrationSpecialAuditorNotice proceed={proceedToIntegrationAppSignIn} />;
  }
  return (
    <div
      className={flex({ alignItems: 'center', padding: '{spacing.20} {spacing.16}', sm: { padding: '{spacing.80}' } })}
    >
      <Card className={css({ backgroundColor: '{colors.background.white}' })}>
        <div
          className={flex({
            padding: '{spacing.20} {spacing.20} {spacing.40} {spacing.20}',
            flexDirection: 'column',
            justifyContent: 'center',
            sm: {
              padding: '{spacing.40}',
            },
          })}
        >
          <h1
            className={css({
              textStyle: 'headding.l',
              paddingTop: '{spacing.24}',
              paddingBottom: '{spacing.16}',
              textAlign: 'center',
              color: '{colors.text.main}',
            })}
          >
            サブメールアドレスの追加
          </h1>
          <p className={css({ textStyle: 'body.m.default', color: '{colors.text.sub}', textAlign: 'center' })}>
            新しいメールアドレスを入力してください。
          </p>
        </div>
        <ChangeForm onSubmit={onSubmit} register={register} formState={formState} />
        <div
          className={flex({
            padding: '0 {spacing.40} {spacing.40}',
            marginTop: '-20px', // ChangeForm の props で調整可能にする
            flexDirection: 'column',
            justifyContent: 'center',
          })}
        >
          <p className={css({ textStyle: 'body.m.default', color: '{colors.text.sub}' })}>
            個人のZEN IDを既にお持ちの方は
            <button
              onClick={showOwnedAccountGuide}
              className={css({
                color: '{colors.action.primary.main}',
                fontWeight: 'bold',
                border: 'none',
                background: 'none',
                textDecoration: 'underline',
                textStyle: 'inherit',
                cursor: 'pointer',
              })}
            >
              こちら
            </button>
          </p>
        </div>
      </Card>
    </div>
  );
}
